/**
 * @file `POST /families/{familyId}/study/task_opening/{personId}` のクライアント。
 *
 * @what: 課題の場面と役を送り、相手役が最初に話しかける一言を1つ受け取る。前に出した一言を
 *   渡すと、それと同じ言い方を避ける。
 * @why: 一言をカリキュラムに固定で書くと、先に読めて聞き取りの練習にならず、同じ一言なので
 *   2回目は必ず通ってしまう (設計 plan/docs/study_skill_task_design.md §5.6)。
 * @failure: 作れなかったときは呼び手がカリキュラムに書いてある一言に落とす。
 */

import { apiFetch } from "@/shared/api/client";
import type { StudyTextbookRouteTask } from "./textbookRoutes";

export interface StudyTaskOpeningInput {
  task_label?: string;
  scene: string;
  role: string;
  avoid?: string[];
}

export interface StudyTaskOpeningResult {
  line: string;
}

export function makeTaskOpening(
  familyId: string,
  personId: string,
  input: StudyTaskOpeningInput,
): Promise<StudyTaskOpeningResult> {
  return apiFetch<StudyTaskOpeningResult>(
    `/families/${encodeURIComponent(familyId)}/study/task_opening/${encodeURIComponent(personId)}`,
    { method: "POST", body: input },
  );
}

/**
 * 課題から、一言を作る材料を組み立てる。
 *
 * @what: 場面と役を渡し、避ける言い回しにカリキュラムに書いてある一言と、前に出した一言を入れる。
 * @why: 書いてある一言を避けるのは、カリキュラムを読んで備えられるのを防ぐためである。
 */
export function taskOpeningInputFrom(
  task: StudyTextbookRouteTask,
  used: readonly string[],
): StudyTaskOpeningInput {
  const avoid = [task.opening ?? "", ...used].filter((s) => s !== "");
  const input: StudyTaskOpeningInput = { scene: task.scene, role: task.role };
  if (task.label) input.task_label = task.label;
  if (avoid.length > 0) input.avoid = avoid.slice(0, 10);
  return input;
}
